// combatpro/spell/spellAiEngine.js
//
// Combat PRO - Spell AI Engine (Stage 49-0: Tactical Target Selection).
//
// Pure, UI-independent tactical target-selection migrated from the legacy
// vi_autoSelectTargets (battletracker.html 11877-11921). This is the leaf of the
// Spell AI runtime: data in, targets out. No React, no DOM, no window, no
// document, no localStorage, no CombatContext, no rendering, no logging, no
// mutation of external state.
//
// The legacy read the module globals `monsters` and `window.viPlayers`; to keep
// this module pure those are INJECTED by the caller (monsters, players), exactly
// as spellEngine / playerEngine inject their data. The spell object is the
// prepared/enriched spell (so any overrides already applied upstream are honoured)
// and is treated as read-only.
//
// SCOPE (Stage 49-0): ONLY tactical target selection. This module performs NO
// spell scoring, NO recommendation, NO role weighting, NO priority calculation,
// NO badges, NO combat log - those are Stage 49-1 / 49-2 / 49-3 and are NOT here.
//
// DEFERRED (NOT referenced by this function, recorded for 49-1): caster.class,
// caster._concentrationSpell, exec.priority. vi_autoSelectTargets does not read
// any of them; they belong to vi_scoreSpellChoice (Stage 49-1).
//
// Behaviour is translated verbatim from the legacy (branch order, the reduce
// tie-breaks that keep the earlier element, the dedup/normalise step). No
// heuristic is simplified or improved.
//
// No optional chaining. No em dash.
//
// Stage 49-2 imports two PURE sibling engines to compose the recommendation
// pipeline (getSpellData resolves a slot name against the injected spellsDb;
// prepareSpellExecution applies monster overrides). These are reuse, not
// duplication, and do not introduce any React / DOM / window dependency - the
// spell database is injected by the caller, never read from window here.

import { getSpellData } from './spellEngine';
import { prepareSpellExecution } from './spellCastEngine';

// Tactical target selection. Mirrors vi_autoSelectTargets(spell, casterIndex)
// with the monster / player arrays injected.
//   spell       - prepared spell object (read-only): target, spellType,
//                 areaOfEffect, range.
//   casterIndex - the casting monster's index.
//   monsters    - array of monster objects (currentHP read).
//   players     - array of player objects ({ id, hp }); may be omitted.
// Returns an array of targets: numbers (monster indices) and / or "p:id" strings
// (players). May be empty (utility spells, or no valid targets).
export function autoSelectTargets(spell, casterIndex, monsters, players) {
  try {
    if (!spell) { return []; }
    // Self-override always wins (legacy 11881).
    if (spell.target === 'self') { return [casterIndex]; }

    const monsterList = monsters || [];
    const playerList = players || [];
    const type = spell.spellType || 'utility';

    const activeEnemies = monsterList.map((m, i) => i).filter(i => {
      if (i === casterIndex) { return false; }
      if (!monsterList[i]) { return false; }
      return (monsterList[i].currentHP || 0) > 0;
    });
    const activePlayers = playerList.filter(p => (p.hp || 0) > 0).map(p => 'p:' + p.id);

    let result = [];
    if (type === 'damage') {
      if (spell.areaOfEffect && activeEnemies.length > 0) {
        result = activeEnemies;
      } else if (activeEnemies.length > 0) {
        result = [activeEnemies.reduce((a, b) => (monsterList[a].currentHP || 0) <= (monsterList[b].currentHP || 0) ? a : b)];
      }
    } else if (type === 'heal') {
      if (spell.areaOfEffect && activePlayers.length > 0) {
        result = activePlayers;
      } else if (activePlayers.length > 0) {
        result = [activePlayers.reduce((a, b) => {
          const aId = String(a).replace('p:', '');
          const bId = String(b).replace('p:', '');
          const aHp = (playerList.find(p => String(p.id) === aId) || {}).hp || 0;
          const bHp = (playerList.find(p => String(p.id) === bId) || {}).hp || 0;
          return aHp <= bHp ? a : b;
        })];
      } else if (activeEnemies.length > 0) {
        result = [activeEnemies[0]];
      }
    } else if (type === 'control') {
      if (activeEnemies.length > 0) {
        result = [activeEnemies.reduce((a, b) => (monsterList[a].currentHP || 0) >= (monsterList[b].currentHP || 0) ? a : b)];
      }
    } else if (type === 'buff') {
      if (spell.range && spell.range.toLowerCase() === 'self') {
        result = [casterIndex];
      } else if (activePlayers.length > 0) {
        result = [activePlayers[0]];
      }
    }
    // utility: no auto target (result stays []).

    if (!Array.isArray(result)) { return []; }
    result = [...new Set(result.map(t => String(t)))].map(t => {
      const n = Number(t);
      return isNaN(n) ? t : n;
    });
    return result;
  } catch (e) {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Stage 49-1 (Spell Scoring): pure migration of vi_scoreSpellChoice
// (battletracker.html 12248-12342). Data in, numeric score out. This produces a
// score ONLY - it does NOT choose a spell, build recommendations, weight by
// recommendation reason, render, or log (those are Stage 49-2 / 49-3).
//
// The legacy module globals (monsters, window.viPlayers) are injected. caster is
// derived as monsters[casterIndex] exactly as the legacy did. Every branch, every
// threshold, and every weight is translated verbatim - no rebalancing, no
// simplification.
//
// DEFERRED legacy fields (read with the legacy fallbacks; population is a 49-2
// data concern, and locked runtime is NOT modified to add them):
//   - execSpell.priority         -> base score (|| 1)
//   - caster.class               -> role bias (|| "" => no bias)
//   - caster._concentrationSpell -> concentration penalty / buff re-cast bonus
//     (set dynamically by castSpell, Stage 48A).
//
// No optional chaining. No em dash. No logging (the legacy catch logged a warning;
// here the catch silently returns -1 to honour the no-logging / pure rule, with
// identical return behaviour).
export function scoreSpellChoice(execSpell, casterIndex, targets, monsters, players) {
  try {
    if (!execSpell) { return -1; }
    const monsterList = monsters || [];
    const playerList = players || [];
    const type = execSpell.spellType || 'utility';
    let score = execSpell.priority || 1;

    const caster = monsterList[casterIndex];
    const casterHp = caster ? (caster.currentHP || 0) : 0;
    const casterMaxHp = caster ? (caster.maxHP || 1) : 1;
    const casterPct = casterHp / casterMaxHp;

    // Resolve live monster targets only (legacy 12260-12262).
    const monsterTargets = Array.isArray(targets)
      ? targets.filter(t => typeof t === 'number' && monsterList[t] && (monsterList[t].currentHP || 0) > 0)
      : [];

    // Role bias from the monster class field (legacy 12265-12268).
    const roleStr = ((caster && caster.class) || '').toLowerCase();
    const isCaster = roleStr.includes('wizard') || roleStr.includes('sorcerer') || roleStr.includes('warlock') || roleStr.includes('mage') || roleStr.includes('lich');
    const isBrute = roleStr.includes('fighter') || roleStr.includes('barbarian') || roleStr.includes('brute') || roleStr.includes('ogre') || roleStr.includes('troll');
    const isSupport = roleStr.includes('cleric') || roleStr.includes('druid') || roleStr.includes('paladin') || roleStr.includes('acolyte') || roleStr.includes('priest');

    // Concentration penalty (legacy 12271-12272).
    const hasActiveConc = !!(caster && caster._concentrationSpell);
    if (execSpell.concentration && hasActiveConc) { score -= 2; }

    if (type === 'damage') {
      if (isBrute) { score += 1; }
      if (execSpell.damageFormula) { score += 1; }
      if (execSpell.areaOfEffect) {
        const activeEnemyCount = monsterList.filter((m, i) => i !== casterIndex && (m.currentHP || 0) > 0).length;
        if (activeEnemyCount >= 3) { score += 4; }
        else if (activeEnemyCount === 2) { score += 2; }
        else { score += 1; }
      }
      if (monsterTargets.length > 0) {
        const tgt = monsterList[monsterTargets[0]];
        const tgtPct = (tgt.currentHP || 0) / (tgt.maxHP || 1);
        if (tgtPct < 0.20) { score += 5; }
        else if (tgtPct < 0.35) { score += 3; }
        else if (tgtPct > 0.75) { score -= 1; }
      }
    } else if (type === 'control') {
      if (isCaster) { score += 1; }
      if (execSpell.appliesCondition) { score += 2; }
      if (execSpell.concentration) {
        if (monsterTargets.length > 0) {
          const tgt = monsterList[monsterTargets[0]];
          const tgtPct = (tgt.currentHP || 0) / (tgt.maxHP || 1);
          if (tgtPct > 0.65) { score += 3; }
          else if (tgtPct > 0.40) { score += 1; }
        }
        if (!hasActiveConc) { score += 1; }
      }
      if (!execSpell.concentration && monsterTargets.length > 0) {
        const tgt = monsterList[monsterTargets[0]];
        const tgtPct = (tgt.currentHP || 0) / (tgt.maxHP || 1);
        if (tgtPct > 0.65) { score += 2; }
      }
    } else if (type === 'heal') {
      if (isSupport) { score += 1; }
      let criticalAlly = null;
      let lowAlly = null;
      for (let i = 0; i < playerList.length; i++) {
        const p = playerList[i];
        if ((p.hp || 0) <= 0) { continue; }
        const pct = (p.hp || 0) / (p.maxHp || 1);
        if (pct < 0.4) { criticalAlly = p; break; }
        if (pct < 0.7 && !lowAlly) { lowAlly = p; }
      }
      if (criticalAlly) {
        score += 5;
        if (criticalAlly.name && criticalAlly.name.trim() !== '') { score += 1; }
      } else if (lowAlly) {
        score += 2;
      } else if (casterPct < 0.4) {
        score += 3;
      }
    } else if (type === 'buff') {
      if (isSupport) { score += 1; }
      if (casterPct > 0.5) { score += 2; }
      const rangeStr = (execSpell.range || '').toLowerCase();
      if (rangeStr === 'self') { score += 2; }
      if (!execSpell.concentration) { score += 1; }
      if (hasActiveConc && execSpell.name === (caster && caster._concentrationSpell)) { score += 2; }
    } else {
      score = Math.max(0, score - 2);
    }

    // No-target penalty for offensive spells (legacy 12336-12338).
    if (type !== 'heal' && type !== 'buff' && type !== 'utility' && monsterTargets.length === 0) {
      score = Math.max(0, score - 3);
    }

    return score;
  } catch (e) {
    return -1;
  }
}

// ---------------------------------------------------------------------------
// Stage 112 (AI Spell Explanation Parity): pure migration of the legacy
// vi_explainSpellChoice (battletracker.html 12345-12381). Data in, reason string
// out. Returns the human-readable recommendation reason for a chosen spell; it
// performs NO scoring, NO selection, NO targeting, NO logging, NO mutation. Every
// returned string is translated verbatim from the legacy (branch order and wording
// preserved exactly). The legacy read the module globals `monsters` and
// `window.viPlayers`; here they are INJECTED (monsters, players), matching the
// autoSelectTargets / scoreSpellChoice convention. The `score` parameter is kept
// for legacy signature parity (the legacy body never reads it either). The
// monsterTargets filter mirrors the legacy explain expression exactly (12349-12351:
// currentHP > 0, no "|| 0"). No optional chaining. No em dash.
export function explainSpellChoice(execSpell, targets, score, monsters, players) {
  try {
    if (!execSpell) { return 'No spell selected.'; }
    const monsterList = monsters || [];
    const playerList = players || [];
    const type = execSpell.spellType || 'utility';
    const monsterTargets = Array.isArray(targets)
      ? targets.filter(t => typeof t === 'number' && monsterList[t] && monsterList[t].currentHP > 0)
      : [];

    if (execSpell.target === 'self') { return 'Self-only override applied.'; }

    if (type === 'damage') {
      if (execSpell.areaOfEffect) { return 'Best AoE damage option against multiple active enemies.'; }
      if (monsterTargets.length > 0) {
        const tgt = monsterList[monsterTargets[0]];
        const pct = (tgt.currentHP || 0) / (tgt.maxHP || 1);
        if (pct < 0.35) { return 'Best damage option against low-HP enemy.'; }
      }
      return 'Best damage option available.';
    }
    if (type === 'control') {
      if (execSpell.appliesCondition) { return 'Control spell applies ' + execSpell.appliesCondition + ' to strongest active enemy.'; }
      return 'Control spell selected against strongest active enemy.';
    }
    if (type === 'heal') {
      const critical = playerList.find(p => (p.hp || 0) > 0 && ((p.hp || 0) / (p.maxHp || 1)) < 0.4);
      if (critical) { return 'Healing selected: ally is critically low HP.'; }
      return 'Healing selected: ally is below 70% HP.';
    }
    if (type === 'buff') {
      const rangeStr = (execSpell.range || '').toLowerCase();
      if (rangeStr === 'self') { return 'Self-buff selected: caster is active and healthy.'; }
      return 'Buff selected for active ally.';
    }
    return 'Utility spell selected: no better combat option available.';
  } catch (e) {
    return 'Spell selected.';
  }
}

// ---------------------------------------------------------------------------
// Stage 49-2 (Recommendation Runtime): pure migration of the legacy
// vi_chooseBestSpellForMonster (battletracker.html 12384-12424). It composes the
// completed runtime - getSpellData (spellEngine) + prepareSpellExecution
// (spellCastEngine) to resolve/enrich each candidate, autoSelectTargets (49-0)
// for tactical targets, and scoreSpellChoice (49-1) for the score - then ranks by
// highest score and returns the best candidate. It duplicates NO targeting or
// scoring logic and moves NO ownership.
//
// Data in, data out: monsters / players / spellsDb are injected (no globals, no
// window, no DOM). caster is monsters[casterIndex], exactly as the legacy.
//
// RETURN SHAPE: { spell, targets, score, reason } or null. The legacy "best"
// carried a `reason` from vi_explainSpellChoice (12344-12381). Stage 49-2 originally
// deferred it (the reason was not generated and explainSpellChoice was not migrated);
// Stage 112 migrated explainSpellChoice (above) and restored the full legacy return
// shape by attaching `reason` at the best-object construction, exactly as legacy did
// (12417-12418). No other behaviour differs from the legacy.
//
// No optional chaining. No em dash. No logging (the legacy catch logged a warning;
// here the catch silently returns null, with identical return behaviour).
export function chooseBestSpellForMonster(casterIndex, monsters, players, spellsDb) {
  try {
    const monsterList = monsters || [];
    const caster = monsterList[casterIndex];
    // Dead casters do not cast (legacy 12387-12388).
    if (!caster || (caster.currentHP || 0) <= 0) { return null; }
    // Must have a spell list (legacy 12390).
    if (!Array.isArray(caster.spells) || caster.spells.length === 0) { return null; }

    let best = null;

    for (let i = 0; i < caster.spells.length; i++) {
      const spellSlot = caster.spells[i];
      // Spell slots are objects { name, castRound }; name is the key (legacy 12396).
      const spellName = (typeof spellSlot === 'string') ? spellSlot : (spellSlot && spellSlot.name);
      if (!spellName || typeof spellName !== 'string' || spellName.trim() === '') { continue; }

      // Resolve + enrich (legacy 12400-12405): missing data does not crash.
      const rawSpell = getSpellData(spellName, spellsDb);
      if (!rawSpell) { continue; }
      const execSpell = prepareSpellExecution(rawSpell, caster);
      if (!execSpell) { continue; }

      // Override-aware tactical targeting (49-0). Skip if no valid targets.
      const targets = autoSelectTargets(execSpell, casterIndex, monsterList, players);
      if (!targets || targets.length === 0) { continue; }

      // Score (49-1). Skip negative scores (legacy 12414).
      const score = scoreSpellChoice(execSpell, casterIndex, targets, monsterList, players);
      if (score < 0) { continue; }

      // Rank: keep the strictly-higher score; ties keep the earlier candidate
      // (legacy 12416: score > best.score).
      if (!best || score > best.score) {
        // Stage 112: attach the recommendation reason exactly as legacy did
        // (12417-12418): explainSpellChoice is called ONLY for the new best (not
        // per candidate), restoring the legacy { spell, targets, score, reason }
        // return shape. Reuses the injected monsterList / players, matching the
        // module's pure-parameter convention.
        const reason = explainSpellChoice(execSpell, targets, score, monsterList, players);
        best = { spell: execSpell, targets: targets, score: score, reason: reason };
      }
    }

    return best; // null if no usable spell
  } catch (e) {
    return null;
  }
}
